import React from 'react';
import { Box, Typography, IconButton, Switch, FormControlLabel, Chip } from '@mui/material';
import Plus from 'src/icons/Plus';
import Minus from 'src/icons/Minus';
import { AIIcon } from 'src/icons/AIIcon';
import { getWordWithNumber } from 'src/utils';

type CustomPlanConfiguratorProps = {
    // Параметры
    projects: number;
    keywords: number;
    bundles: number;
    chats: number;

    // Минимальные значения (из параметров мониторинга)
    minKeywords: number;
    minBundles: number;
    minChats: number;

    // Обработчики изменения
    onProjectsChange: (value: number) => void;
    onKeywordsChange: (value: number) => void;
    onBundlesChange: (value: number) => void;
    onChatsChange: (value: number) => void;

    // Опции
    aiAnalysisEnabled: boolean;
    onAIAnalysisChange: (enabled: boolean) => void;
    aiAnalysisPrice: number;

    leadsExportEnabled: boolean;
    onLeadsExportChange: (enabled: boolean) => void;

    // Цены
    projectPrice: number;
    keywordPrice: number;
    bundlePrice: number;
    chatPrice: number;
    basePrice: number;
};

export default function CustomPlanConfigurator({
    projects,
    keywords,
    bundles,
    chats,
    minKeywords,
    minBundles,
    minChats,
    onProjectsChange,
    onKeywordsChange,
    onBundlesChange,
    onChatsChange,
    aiAnalysisEnabled,
    onAIAnalysisChange,
    aiAnalysisPrice,
    leadsExportEnabled,
    onLeadsExportChange,
    projectPrice,
    keywordPrice,
    bundlePrice,
    chatPrice,
    basePrice,
}: CustomPlanConfiguratorProps) {
    const ParameterControl = ({
        label,
        value,
        onChange,
        minValue = 0,
        chipLabel,
    }: {
        label: string;
        value: number;
        onChange: (value: number) => void;
        minValue?: number;
        chipLabel?: string;
    }) => (
        <Box
            display="flex"
            alignItems="center"
            gap={0.5}
        >
            <Chip
                label={chipLabel || `${value} ${label.toLowerCase()}`}
                size="small"
                sx={{
                    border: '1.5px solid #0044FF',
                    color: '#000',
                    background: 'rgba(0, 68, 255, 0.08)',
                    fontWeight: 600,
                    borderRadius: '12px',
                    fontSize: 15,
                    height: '28px',
                }}
            />
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    if (value > minValue) {
                        onChange(value - 1);
                    }
                }}
                disabled={value <= minValue}
                sx={{
                    border: '1px solid #E3ECF3',
                    borderRadius: '6px',
                    width: '28px',
                    height: '28px',
                    padding: 0,
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    },
                    '&.Mui-disabled': {
                        opacity: 0.4,
                    },
                }}
            >
                <Box sx={{ '& svg': { width: '16px', height: '16px' } }}>
                    <Minus />
                </Box>
            </IconButton>
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    onChange(value + 1);
                }}
                sx={{
                    border: '1px solid #E3ECF3',
                    borderRadius: '6px',
                    width: '28px',
                    height: '28px',
                    padding: 0,
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    },
                }}
            >
                <Box sx={{ '& svg': { width: '16px', height: '16px' } }}>
                    <Plus />
                </Box>
            </IconButton>
        </Box>
    );

    return (
        <Box
            mt={1.5}
            pt={1.5}
            sx={{
                borderTop: '1px solid #E3ECF3',
            }}
        >
            {/* Параметры как лейблы */}
            <Box
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                gap={1}
                alignItems="center"
            >
                <ParameterControl
                    label="проект"
                    value={projects}
                    onChange={onProjectsChange}
                    minValue={1}
                    chipLabel={getWordWithNumber(projects, ['проект', 'проекта', 'проектов'])}
                />
                <Box
                    sx={{
                        width: '1px',
                        height: '28px',
                        backgroundColor: '#E3ECF3',
                        mx: 1,
                    }}
                />
                <ParameterControl
                    label="ключевое слово"
                    value={keywords}
                    onChange={onKeywordsChange}
                    minValue={minKeywords}
                    chipLabel={getWordWithNumber(keywords, ['ключевое слово', 'ключевых слова', 'ключевых слов'])}
                />
                <Box
                    sx={{
                        width: '1px',
                        height: '28px',
                        backgroundColor: '#E3ECF3',
                        mx: 1,
                    }}
                />
                <ParameterControl
                    label="подборка"
                    value={bundles}
                    onChange={onBundlesChange}
                    minValue={minBundles}
                    chipLabel={getWordWithNumber(bundles, ['подборка', 'подборки', 'подборок'])}
                />
                <Box
                    sx={{
                        width: '1px',
                        height: '28px',
                        backgroundColor: '#E3ECF3',
                        mx: 1,
                    }}
                />
                <ParameterControl
                    label="чат"
                    value={chats}
                    onChange={onChatsChange}
                    minValue={minChats}
                    chipLabel={getWordWithNumber(chats, ['чат', 'чата', 'чатов'])}
                />
            </Box>

            {/* Опции */}
            <Box mt={1.5}>
                <Box
                    sx={{
                        border: '1px solid #E3ECF3',
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                        p: 1.5,
                    }}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={aiAnalysisEnabled}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    onAIAnalysisChange(e.target.checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                color="primary"
                                size="small"
                            />
                        }
                        label={
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.75}
                            >
                                <AIIcon
                                    width={16}
                                    height={16}
                                />
                                <Typography
                                    variant="body2"
                                    color="primary"
                                    sx={{ fontWeight: 600, fontSize: '14px' }}
                                >
                                    ИИ анализ лидов
                                </Typography>
                            </Box>
                        }
                        onClick={(e) => e.stopPropagation()}
                    />
                </Box>
            </Box>
        </Box>
    );
}
